import { useQuery } from '@apollo/client'
import { FC, useCallback } from 'react'

import { useActions as useFilterActions } from 'components/contexts/event-page-filters'
import { useActions as useEventPageActions } from 'components/contexts/event-page'
import { generateTicketTitle, getTicketDeliveryNotes } from 'utils/tevo'
import { GetEventAuctionByIdQuery, TevoTicketsQuery } from 'gql'
import { Flex, Spinner } from 'components/ui'
import { formatCurrency } from 'utils'
import { useDataLayer } from 'hooks'
import { differenceInMilliseconds, format } from 'date-fns'
import { GET_EVENT_AUCTION_BY_ID } from 'gql/artwork/queries'
import Ticket, { TicketProps } from './ticket'

type TevoTicketsProps = {
  data: TevoTicketsQuery['findArtwork']['tevoTicketGroups']
  artworkPk: string
  loading?: boolean
  withFlashSale?: boolean
}

const TevoTickets: FC<TevoTicketsProps> = ({ artworkPk, data, loading, withFlashSale = false }) => {
  const { data: eventData } = useQuery<GetEventAuctionByIdQuery>(GET_EVENT_AUCTION_BY_ID, {
    variables: {
      pk: artworkPk,
    },
  })

  const { onAddToCheckout } = useEventPageActions()
  const { setFormQuantityOptions } = useFilterActions()

  const dl = useDataLayer()

  const getTicketProps = useCallback(
    (item): Partial<TicketProps> => {
      if (withFlashSale && item.discount) {
        return {
          helper: {
            text: format(
              differenceInMilliseconds(new Date(item.discount.expiresOn), new Date()),
              "'Flash sale!' H'h':mm'm' 'remaining!'"
            ),
            glowColor: 'green',
            icon: 'lightning-v2',
          },
          prevPrice: item.discount ? item.discount.discountedPrice : undefined,
        }
      }

      return {}
    },
    [withFlashSale]
  )

  if (loading) {
    return (
      <Flex padding="1rem" justifyContent="center">
        <Spinner />
      </Flex>
    )
  }

  return (
    <>
      {data.map((item) => (
        <Ticket
          {...getTicketProps(item)}
          title={generateTicketTitle(item.section, item.row)}
          quantity={item.availableQuantity}
          key={item.id}
          price={item.retailPrice}
          onClick={() => {
            setFormQuantityOptions(item.splits)

            onAddToCheckout({
              title: generateTicketTitle(item.section, item.row),
              deliveryNotes: getTicketDeliveryNotes(item.format),
              price: `${formatCurrency(item.retailPrice, 0)}/ea`,
              unformatedPrice: item.retailPrice,
              messages: [],
              checkoutPayload: {
                tevoTicketGroupId: parseInt(item.id, 10),
              },
              tevoSectionName: item.tevoSectionName,
              publicNotes: item.publicNotes,
            })

            const event = eventData.findArtwork || ({} as GetEventAuctionByIdQuery['findArtwork'])

            dl.push({
              event: 'view_item',
              ecommerce: {
                currency: 'USD',
                value: item.retailPrice,
                item_list_name: 'tickets',
                items: [
                  {
                    item_name: generateTicketTitle(item.section, item.row),
                    item_id: item.id,
                    star_name: event.mainArtist?.fullName,
                    event_date: event.eventStartDateLocal,
                    venue_name: event.venue?.name,
                    tour_name: event.tour?.title,
                    event_name: event.title,
                  },
                ],
              },
            })
          }}
        />
      ))}
    </>
  )
}

export default TevoTickets
